/**
 * Baki Apothecary behaviours. Progressive enhancement only: every section renders fully without this file.
 * Homepage (body.baki--home): scroll reveals, word-by-word headlines, tube liquid rise, hero arch slides,
 * quote carousel. Everywhere: countdowns, underline tabs, delivery picker, marquee cloning.
 * Respects prefers-reduced-motion.
 */
(function () {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const home = document.body.classList.contains('baki--home');

  /* Word-by-word headlines: split text nodes into masked words. */
  function splitWords(el) {
    if (el.dataset.split) return;
    el.dataset.split = '1';
    const text = el.textContent.trim();
    el.textContent = '';
    text.split(/\s+/).forEach((w, i) => {
      const outer = document.createElement('span');
      outer.className = 'words__w';
      const inner = document.createElement('span');
      inner.textContent = w;
      inner.style.transitionDelay = i * 70 + 'ms';
      outer.appendChild(inner);
      el.appendChild(outer);
    });
  }

  function initReveals(root) {
    const targets = root.querySelectorAll('[data-rv], .tube');
    if (!targets.length) return;
    if (!home || reduce || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }
    root.querySelectorAll('[data-words]').forEach(splitWords);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    targets.forEach((el) => io.observe(el));
  }

  /* Hero arch: cross-fade slides. */
  function initArch(root) {
    root.querySelectorAll('.arch').forEach((arch) => {
      const imgs = arch.querySelectorAll('.arch__win img');
      const dots = arch.querySelectorAll('.arch__dots button');
      if (imgs.length < 2) { imgs[0] && imgs[0].classList.add('is-on'); return; }
      let k = 0, timer;
      const show = (i) => {
        k = i;
        imgs.forEach((im, j) => im.classList.toggle('is-on', j === i));
        dots.forEach((d, j) => d.classList.toggle('is-on', j === i));
      };
      const start = () => { if (reduce) return; clearInterval(timer); timer = setInterval(() => show((k + 1) % imgs.length), 3800); };
      dots.forEach((d, i) => d.addEventListener('click', () => { show(i); start(); }));
      show(0); start();
    });
  }

  /* Quote carousel. */
  function initQuotes(root) {
    root.querySelectorAll('.rq').forEach((rq) => {
      const qs = rq.querySelectorAll('.rq__q');
      const nav = rq.querySelectorAll('.rq__nav button');
      if (qs.length < 2) return;
      let k = 0, timer;
      const show = (i) => {
        k = i;
        qs.forEach((q, j) => { q.classList.toggle('is-on', j === i); q.setAttribute('aria-hidden', j !== i); });
        nav.forEach((b, j) => b.classList.toggle('is-on', j === i));
      };
      const start = () => { clearInterval(timer); if (!reduce) timer = setInterval(() => show((k + 1) % qs.length), 5200); };
      nav.forEach((b, i) => b.addEventListener('click', () => { show(i); start(); }));
      show(0); start();
    });
  }

  /* Countdown to a date (data-ends ISO string), else to tonight's midnight (data-daily) or Sunday midnight. */
  function initCountdown(root) {
    root.querySelectorAll('[data-countdown]').forEach((cd) => {
      /* Count down to the deal's end date (set in the section, or the deal sync's shop metafield from the PMO sheet).
         With no date, or one that has already passed, fall back to the coming Sunday at midnight. */
      const raw = cd.dataset.ends;
      let end = raw ? new Date(raw).getTime() : NaN;
      if (isNaN(end) || end <= Date.now()) {
        const d = new Date();
        if (!('daily' in cd.dataset)) d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
        d.setHours(23, 59, 59, 0); end = d.getTime();
      }
      const cells = cd.querySelectorAll('[data-unit]');
      let timer = null;
      const tick = () => {
        const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (s === 0) { cd.hidden = true; clearInterval(timer); return; }
        const v = { days: Math.floor(s / 86400), hrs: Math.floor((s % 86400) / 3600), min: Math.floor((s % 3600) / 60), sec: s % 60 };
        cells.forEach((c) => { c.textContent = String(v[c.dataset.unit] || 0).padStart(2, '0'); });
      };
      tick(); timer = setInterval(tick, 1000);
    });
  }

  /* Underline tabs switching panels inside a section. */
  function initTabs(root) {
    root.querySelectorAll('[data-baki-tabs]').forEach((wrap) => {
      const tabs = wrap.querySelectorAll('.v4tabs button');
      const panels = wrap.querySelectorAll('[data-tab-panel]');
      tabs.forEach((t) => t.addEventListener('click', () => {
        tabs.forEach((x) => { x.classList.toggle('is-on', x === t); x.setAttribute('aria-selected', x === t); });
        panels.forEach((p) => { p.hidden = p.dataset.tabPanel !== t.dataset.tab; });
        t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        initReveals(wrap);
      }));
    });
  }

  /* Delivery picker zones. */
  function initDelivery(root) {
    root.querySelectorAll('.dl4').forEach((dl) => {
      const btns = dl.querySelectorAll('.dl4__z button');
      const rows = dl.querySelectorAll('.dl4__r');
      btns.forEach((b) => b.addEventListener('click', () => {
        btns.forEach((x) => { x.classList.toggle('is-on', x === b); x.setAttribute('aria-selected', x === b); });
        rows.forEach((r) => r.classList.toggle('is-on', r.dataset.zone === b.dataset.zone));
      }));
    });
  }

  /* Marquee, social rail and deals bar need their track duplicated once for a seamless loop.
     The copy is hidden from screen readers and keyboard. */
  function initMarquee(root) {
    root.querySelectorAll('.v4mq__t, .ug__tr, .db4__t').forEach((track) => {
      if (track.dataset.cloned) return;
      track.dataset.cloned = '1';
      Array.from(track.children).forEach((child) => {
        const copy = child.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        if (copy.matches('a, button')) copy.setAttribute('tabindex', '-1');
        copy.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        track.appendChild(copy);
      });
    });
  }

  /* Scheduled promo banners and deals: the page carries the next few days of Promo banner entries, each with its start and
     end (epoch seconds). Drop those not live in this visitor's browser, so a cached page still switches on time. When a
     scheduled banner is live the editor's everyday banners step aside; slides are put in the sheet's order; the slider
     controls go when one banner is left. Runs before the slider and marquee read their slides. */
  function initBannerSchedule(root) {
    const now = Date.now() / 1000;
    root.querySelectorAll('[data-pb4-start]').forEach((el) => {
      if (now < Number(el.dataset.pb4Start) || now >= Number(el.dataset.pb4End)) el.remove();
    });
    root.querySelectorAll('[data-pb4-sched]').forEach((list) => {
      const live = Array.from(list.querySelectorAll('[data-pb4-start]'));
      if (live.length) list.querySelectorAll('[data-pb4-fallback]').forEach((el) => el.remove());
      live.sort((a, b) => Number(a.dataset.pb4Order) - Number(b.dataset.pb4Order)).forEach((el) => list.appendChild(el));
      const n = list.children.length;
      const sec = list.closest('section');
      if (!n) { if (sec) sec.hidden = true; return; }
      const sl = list.closest('[data-pb4-slider]');
      if (!sl) return;
      const dots = sl.querySelector('.pb4__dots');
      if (n < 2) {
        sl.removeAttribute('data-pb4-slider');
        sl.querySelectorAll('.pb4__arw, .pb4__dots').forEach((el) => el.remove());
      } else if (dots) {
        Array.from(dots.children).forEach((d, i) => { if (i >= n) d.remove(); });
      }
    });
  }

  /* Promo banner slider: arrows, dots, swipe (native scroll-snap) and auto-advance.
     Auto-advance pauses on hover, focus, touch and hidden tabs; off under reduced motion. */
  function initBannerSlider(root) {
    root.querySelectorAll('[data-pb4-slider]').forEach((sl) => {
      if (sl.dataset.ready) return;
      sl.dataset.ready = '1';
      const track = sl.querySelector('.pb4__track');
      const slides = track ? track.children : [];
      const dots = sl.querySelectorAll('[data-pb4-dot]');
      if (!track || slides.length < 2) return;
      const delay = parseInt(sl.dataset.autoplay, 10) * 1000;
      let k = 0, timer, paused = false;
      const go = (i) => {
        k = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[k].offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      };
      const mark = () => {
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        k = Math.min(slides.length - 1, Math.max(0, i));
        dots.forEach((d, j) => { d.classList.toggle('is-on', j === k); if (j === k) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      };
      const start = () => { clearInterval(timer); if (reduce || !delay) return; timer = setInterval(() => { if (!paused && !document.hidden) go(k + 1); }, delay); };
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); }, { passive: true });
      sl.querySelector('[data-pb4-prev]').addEventListener('click', () => { go(k - 1); start(); });
      sl.querySelector('[data-pb4-next]').addEventListener('click', () => { go(k + 1); start(); });
      dots.forEach((d, i) => d.addEventListener('click', () => { go(i); start(); }));
      sl.addEventListener('mouseenter', () => { paused = true; });
      sl.addEventListener('mouseleave', () => { paused = false; });
      sl.addEventListener('focusin', () => { paused = true; });
      sl.addEventListener('focusout', () => { paused = false; });
      track.addEventListener('touchstart', () => { start(); }, { passive: true });
      start();
    });
  }

  /* Bag recommendations: ask Shopify for complementary products for what is in the cart, fall back to related. */
  function initCartRecs() {
    document.querySelectorAll('baki-cart-recs:not([data-ready])').forEach(async function (el) {
      el.dataset.ready = '1';
      var ids = (el.dataset.cartIds || '').split(',').filter(Boolean);
      var limit = parseInt(el.dataset.limit || '4', 10);
      var grid = el.querySelector('[data-cart-recs-grid]');
      if (!ids.length || !grid) return;
      var seen = {}; ids.forEach(function (id) { seen[id] = true; });
      var intents = el.dataset.intent === 'related' ? ['related'] : ['complementary', 'related'];
      var cards = [];
      for (var i = 0; i < ids.length && i < 3 && cards.length < limit; i++) {
        for (var j = 0; j < intents.length && cards.length < limit; j++) {
          try {
            var res = await fetch(el.dataset.url + '&product_id=' + ids[i] + '&intent=' + intents[j]);
            if (!res.ok) continue;
            var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            var found = doc.querySelectorAll('.vl[data-product-id]:not(.vl--sold-out)');
            found.forEach(function (card) {
              var pid = card.dataset.productId;
              if (!pid || seen[pid] || cards.length >= limit) return;
              seen[pid] = true; cards.push(card);
            });
            if (found.length) break; /* this intent answered; no need to fall back for this product */
          } catch (e) { /* network hiccup: try the next source */ }
        }
      }
      if (!cards.length) { el.remove(); return; }
      cards.forEach(function (card, k) { card.style.setProperty('--d', (k * 70) + 'ms'); grid.appendChild(card); });
      el.hidden = false;
    });
  }

  /* Continue shopping: remember the last collection visited so the bag can send people back to it. */
  function initContinueShopping() {
    try {
      var m = location.pathname.match(/^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\/[^/?#]+/i);
      if (m) sessionStorage.setItem('baki:last-collection', location.pathname);
      var last = sessionStorage.getItem('baki:last-collection');
      if (last) document.querySelectorAll('[data-baki-continue]').forEach(function (a) { a.href = last; });
    } catch (e) { /* storage blocked: the fallback link stays */ }
  }

  function init(root) {
    root = root || document;
    initBannerSchedule(root); initMarquee(root); initBannerSlider(root); initReveals(root); initArch(root); initQuotes(root); initCountdown(root); initTabs(root); initDelivery(root); initCartRecs(); initContinueShopping();
  }

  init();
  /* The cart drawer is re-rendered by Horizon after every cart change; pick up the fresh recommendations rail. */
  var recsTimer = null;
  new MutationObserver(function () {
    if (!document.querySelector('baki-cart-recs:not([data-ready])')) return;
    clearTimeout(recsTimer); recsTimer = setTimeout(initCartRecs, 60);
  }).observe(document.body, { childList: true, subtree: true });
  /* Theme editor: re-run when a section is (re)loaded. */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
